import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { useLenis } from '@/contexts/lenis'
import { useTheme } from '@/contexts/theme'
import { useTransitionStore } from '@/contexts/transition'
import { useMotionTouchMedia } from '@/hooks'
import { useLayoutStore } from '@/store/layout'

import { createFadeMotion, createLinesMotion, createMaskMotion } from './shared'

export const useHeroMotion = () => {
  const { theme } = useTheme()
  const { lenis } = useLenis()
  const motionRef = useRef<HTMLDivElement>(null)
  const [landing, reveal] = useTransitionStore((state) => [state.landing, state.reveal])
  const timeline = useRef<GSAPTimeline | null>(null)
  const scrollTrigger = useRef<ScrollTrigger>(null!)
  const setLayoutState = useLayoutStore((state) => state.setState)

  /**
   * reveal setup
   */
  useGSAP(
    ({ selector }) => {
      console.log('reveal setup', lenis)
      if (!lenis) return

      const title = selector?.('[data-motion-title]')[0]
      const pretitle = selector?.('[data-motion-pretitle]')[0]
      const paragraph = selector?.('[data-motion-paragraph]')[0]

      const titleMotion = title ? createFadeMotion(title) : null
      const pretitleMotion = pretitle ? createMaskMotion(pretitle) : null
      const paragraphMotion = paragraph ? createLinesMotion(paragraph) : null

      const revertSplitText = () => {
        titleMotion?.revert()
        pretitleMotion?.revert()
        paragraphMotion?.revert()
      }

      timeline.current = gsap.timeline({
        paused: true,
        onComplete: revertSplitText
      })
      titleMotion && timeline.current.add(titleMotion.timeline, 0)
      pretitleMotion && timeline.current.add(pretitleMotion.timeline, 0)
      paragraphMotion && timeline.current.add(paragraphMotion.timeline, 0)

      return () => {
        timeline.current?.revert()
        revertSplitText()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  )

  /**
   * parallax setup
   */
  useMotionTouchMedia(
    {
      pointer() {
        if (!lenis) return

        scrollTrigger.current = ScrollTrigger.create({
          trigger: '[data-motion-media-mask]',
          start: 'top top',
          markers: false,
          pin: true,
          scrub: true,
          pinSpacing: false,
          animation: gsap
            .timeline()
            .fromTo(
              '[data-motion-media-mask]',
              { yPercent: 0 },
              { yPercent: -100, duration: 1, ease: 'none' }
            )
            .fromTo(
              '[data-motion-media]',
              { yPercent: 0 },
              { yPercent: 100, duration: 1, ease: 'none' },
              0
            )
        })
      },

      revert() {
        scrollTrigger.current?.kill()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  )
  /* useGSAP(
    () => {
      if (!lenis) return

      const scrollTrigger = ScrollTrigger.create({
        trigger: '[data-motion-media-mask]',
        start: 'top top',
        markers: false,
        pin: true,
        scrub: true,
        pinSpacing: false,
        animation: gsap
          .timeline()
          .fromTo(
            '[data-motion-media-mask]',
            { yPercent: 0 },
            { yPercent: -100, duration: 1, ease: 'none' }
          )
          .fromTo(
            '[data-motion-media]',
            { yPercent: 0 },
            { yPercent: 100, duration: 1, ease: 'none' },
            0
          )
      })

      return () => {
        scrollTrigger.kill()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  ) */

  /**
   * theme update setup
   */
  useGSAP(
    () => {
      const scrollTrigger = ScrollTrigger.create({
        trigger: motionRef.current,
        start: 'top+=10% top',
        markers: false,
        onEnter: () => setLayoutState({ footerTheme: theme.footer, srollDownHidden: true }),
        onLeaveBack: () => setLayoutState({ footerTheme: theme.header, srollDownHidden: false })
      })

      return () => {
        scrollTrigger.kill()
      }
    },
    { scope: motionRef, dependencies: [theme], revertOnUpdate: true }
  )

  /**
   * reveal update
   */
  useGSAP(
    () => {
      console.log('reveal update', landing, reveal)
      if (landing || reveal) {
        timeline.current?.play().delay(0)
      }
    },
    { scope: motionRef, dependencies: [landing, reveal] }
  )

  return { motionRef }
}
